import React from 'react';
import { Droplet, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров расчета
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  ageValue
}) => {
  // Расчет текущего веса
  const weight = parseFloat(formData.weight) || 70;
  
  // Расчет текущего уровня активности
  const activityLevel = formData.activityLevel || 'moderate';
  
  // Обработчик изменения веса
  const handleWeightChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      weight: newValue
    }));
  };
  
  // Обработчик изменения возраста
  const handleAgeChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      age: newValue
    }));
  };
  
  // Функция расчета
  const calculateWaterIntake = () => {
    // Здесь должна быть вызвана функция основного расчета
    // Эмулируем действие, так как основная логика находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Быстрая установка веса
  const setQuickWeight = (value) => {
    setFormData(prev => ({
      ...prev,
      weight: value.toString()
    }));
  };
  
  // Быстрая установка уровня активности
  const setQuickActivity = (value) => {
    setFormData(prev => ({
      ...prev,
      activityLevel: value
    }));
  };

  // Быстрая установка пола
  const setQuickGender = (value) => {
    setFormData(prev => ({
      ...prev,
      gender: value,
      // Сбрасываем параметры беременности при смене пола на мужской
      pregnancyLactation: value === 'male' ? 'none' : prev.pregnancyLactation
    }));
  };
  
  // Быстрая установка климата
  const setQuickClimate = (value) => {
    setFormData(prev => ({
      ...prev,
      climate: value
    }));
  };
  
  // Прогноз потребления воды на основе текущих параметров
  const getWaterPrediction = () => {
    // Примерный расчет для отображения в слайдере
    let baseAmount = weight * 33; // ~33 мл на кг веса
    
    // Коэффициенты активности
    const activityFactors = {
      sedentary: 1.0,
      light: 1.1,
      moderate: 1.2,
      active: 1.3,
      very_active: 1.4
    };
    
    // Коэффициенты климата
    const climateFactors = {
      cold: 0.9,
      temperate: 1.0,
      hot: 1.1
    };
    
    // Коэффициенты пола
    const genderFactor = formData.gender === 'male' ? 1.0 : 0.9;
    
    // Применяем коэффициенты
    baseAmount *= activityFactors[activityLevel] || 1.2;
    baseAmount *= climateFactors[formData.climate] || 1.0;
    baseAmount *= genderFactor;
    
    return Math.round(baseAmount);
  };
  
  // Расчет предположительного потребления воды
  const predictedWaterIntake = getWaterPrediction();
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-blue-500" />
        Быстрая настройка параметров
      </h3>
      
      <div className="space-y-5">
        {/* Вес */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="weightSlider" className="text-gray-600 dark:text-gray-400">Вес</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatNumber(weight, 0)} кг</span>
          </div>
          <input
            type="range"
            id="weightSlider"
            min="40"
            max="150"
            step="1"
            value={weight}
            onChange={handleWeightChange}
            aria-label="Вес в килограммах"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          
          {/* Кнопки с предопределенными весами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['50', '60', '70', '80'].map(weightValue => (
              <button
                key={weightValue}
                type="button"
                onClick={() => setQuickWeight(weightValue)}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.weight === weightValue 
                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {ageValue} лет
              </button>
            ))}
          </div>
        </div>
        
        {/* Пол */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Пол
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setQuickGender('male')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.gender === 'male' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Мужской</div>
            </button>
            <button
              type="button"
              onClick={() => setQuickGender('female')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.gender === 'female' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Женский</div>
            </button>
          </div>
        </div>
        
        {/* Активность */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Уровень активности
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <button
              type="button"
              onClick={() => setQuickActivity('sedentary')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.activityLevel === 'sedentary' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Малоподвижный</div>
              <div className="text-xs opacity-75">Сидячая работа</div>
            </button>
            <button
              type="button"
              onClick={() => setQuickActivity('light')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.activityLevel === 'light' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Легкая</div>
              <div className="text-xs opacity-75">Прогулки 1-3 раза</div>
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setQuickActivity('moderate')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.activityLevel === 'moderate' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Умеренная</div>
              <div className="text-xs opacity-75">3-5 раз</div>
            </button>
            <button
              type="button"
              onClick={() => setQuickActivity('active')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.activityLevel === 'active' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Активная</div>
              <div className="text-xs opacity-75">6-7 раз</div>
            </button>
            <button
              type="button"
              onClick={() => setQuickActivity('very_active')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.activityLevel === 'very_active' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Интенсивная</div>
              <div className="text-xs opacity-75">Ежедневно</div>
            </button>
          </div>
        </div>
        
        {/* Климат */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Климат
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setQuickClimate('cold')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.climate === 'cold' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Холодный</div>
              <div className="text-xs opacity-75">до +10°C</div>
            </button>
            <button
              type="button"
              onClick={() => setQuickClimate('temperate')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.climate === 'temperate' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Умеренный</div>
              <div className="text-xs opacity-75">+10-25°C</div>
            </button>
            <button
              type="button"
              onClick={() => setQuickClimate('hot')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.climate === 'hot' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Жаркий</div>
              <div className="text-xs opacity-75">выше +25°C</div>
            </button>
          </div>
        </div>
        
        {/* Прогноз */}
        <div className="water-wave rounded-lg bg-blue-50 dark:bg-blue-900/30 p-4 border border-blue-200 dark:border-blue-800" style={{'--wave-height': '70%'}}>
          <div className="water-wave-content">
            <div className="text-blue-700 dark:text-blue-300 font-medium mb-1 flex items-center gap-2">
              <Droplet className="w-4 h-4" />
              Примерный прогноз
            </div>
            <div className="text-2xl font-semibold text-blue-800 dark:text-blue-200">
              ~{formatNumber(predictedWaterIntake, 0)} мл
            </div>
            <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
              Нажмите кнопку ниже для точного расчета
            </div>
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateWaterIntake}
          icon={<Droplet className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать норму воды
        </Button>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return two;
  }
  return five;
};

export default QuickAdjustmentSidebar;